import { useMutation } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, Truck } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { Seo } from "../components/Seo";

type Tracked = {
  orderNumber: string; status: string; customerName: string; fulfillmentType: string; totalCentavos: number; createdAt: string;
  items: Array<{ name: string; quantity: number }>;
  timeline: Array<{ status: string; message: string; at: string }>;
  payment: null | { status: string; rejectionReason: string | null; canResubmit: boolean };
  shipment: null | { courier_name: string; tracking_number: string; tracking_url: string };
};

export function TrackPage() {
  const [orderNumber, setOrderNumber] = useState(""); const [contact, setContact] = useState("");
  const tracking = useMutation({ mutationFn: () => api<Tracked>("/orders/track", { method: "POST", body: JSON.stringify({ orderNumber, contact }) }) });
  const order = tracking.data;
  return <section className="container-page pb-10 pt-10 md:pt-16">
    <Seo title="Track your order" description="Track your Minis by Cass order using your order number and the email or mobile number from checkout." path="/track" />
    <div className="mx-auto max-w-3xl">
      <h1 className="heading">Track an order</h1>
      <p className="mt-4 max-w-[52ch] text-muted">Enter your order number and the email or mobile number you used at checkout.</p>

      <form onSubmit={(e) => { e.preventDefault(); tracking.mutate(); }} className="mt-8 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <label><span className="label">Order number</span><input required className="field uppercase placeholder:normal-case" placeholder="CRFT-20260729-0001" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} /></label>
        <label><span className="label">Email or mobile</span><input required className="field" value={contact} onChange={(e) => setContact(e.target.value)} /></label>
        <button className="btn-primary" disabled={tracking.isPending}>{tracking.isPending ? "Looking…" : "Find order"}</button>
      </form>
      {tracking.isError && <p role="alert" className="mt-5 rounded-[10px] bg-blush/50 px-4 py-3 font-bold text-ribbon-ink">Those details don’t match an order. Check the order number and the email or mobile you used.</p>}

      {order && <div className="mt-12 [animation:fade-in_.3s_ease-out]">
        <div className="ruler" aria-hidden="true" />
        <div className="mt-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-muted">{order.orderNumber}</p>
            <h2 className="mt-1 font-display text-4xl leading-none text-cocoa">{friendly(order.status)}</h2>
            <p className="mt-3 text-muted">For {order.customerName}, {order.fulfillmentType === "PICKUP" ? "pickup" : "delivery"}</p>
          </div>
          <p className="font-display text-2xl tabular-nums">{formatMoney(order.totalCentavos)}</p>
        </div>
        <ul className="mt-5 flex flex-wrap gap-2">{order.items.map((item) => <li key={item.name} className="rounded-[4px] bg-frosting px-3 py-1.5 text-[15px]"><b>{item.quantity}×</b> {item.name}</li>)}</ul>

        {order.payment?.canResubmit && <div className="mt-8 flex gap-3 rounded-xl border-[1.5px] border-ribbon/40 bg-blush/25 p-5">
          <AlertCircle className="mt-0.5 shrink-0 text-ribbon-ink" />
          <div>
            <h3 className="text-lg font-bold">Please send a new payment receipt</h3>
            {order.payment.rejectionReason && <p className="mt-1 text-cocoa/80">{order.payment.rejectionReason}</p>}
            <Link to={`/payment/${order.orderNumber}`} className="btn-primary mt-4">Upload a new receipt <ArrowRight size={17} /></Link>
          </div>
        </div>}

        {order.shipment && <div className="mt-8 flex gap-4 rounded-xl bg-frosting p-5">
          <Truck className="mt-0.5 shrink-0 text-cocoa" />
          <div>
            <h3 className="font-bold">{order.shipment.courier_name}</h3>
            <p className="tabular-nums text-muted">{order.shipment.tracking_number}</p>
            {order.shipment.tracking_url && <a className="link-quiet mt-2 text-sm" href={order.shipment.tracking_url} target="_blank" rel="noreferrer">Open courier tracking</a>}
          </div>
        </div>}

        <h3 className="mt-12 text-lg font-bold">Order history</h3>
        <ol className="mt-6">
          {[...order.timeline].reverse().map((event, index, list) => <li key={`${event.status}-${event.at}`} className="relative flex gap-5 pb-8 last:pb-0">
            {index < list.length - 1 && <span className="absolute left-[7px] top-5 h-full w-px bg-line" aria-hidden="true" />}
            <span className={`relative z-10 mt-1.5 size-[15px] shrink-0 rounded-full ${index === 0 ? "bg-ribbon ring-4 ring-blush/60" : "border-[1.5px] border-cocoa/30 bg-white"}`} aria-hidden="true" />
            <div>
              <p className={`font-bold ${index === 0 ? "text-cocoa" : "text-cocoa/75"}`}>{friendly(event.status)}</p>
              <p className="mt-0.5 text-[15px] text-muted">{event.message}</p>
              <time className="mt-1 block text-[13px] text-muted/80">{new Date(event.at).toLocaleString()}</time>
            </div>
          </li>)}
        </ol>
      </div>}
    </div>
  </section>;
}

function friendly(status: string) {
  const text = status.toLowerCase().replaceAll("_", " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}
